import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Compass, Loader2, X, ChevronRight, RefreshCw, Check,
} from "lucide-react";

/**
 * DayZeroChat — D5a (iter134).
 *
 * A branching questionnaire in a chat wrapper. Zero LLM. All steps,
 * options and validation come from the server — this component just
 * renders bubbles and forwards user picks.
 *
 * Props:
 *   variant: "full" (default, /coach) | "mini" (/dashboard)
 *     Mini: single collapsed row linking to /coach. Full: expanded
 *     inline chat card.
 *   onCompleted?: () => void
 */
export default function DayZeroChat({ variant = "full", onCompleted }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/day-zero");
      setState(data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-open on first render if the profile hasn't started yet.
  // Gentle: this only pushes the assistant's opening bubble to the
  // server side — no modal, no scroll-jack, no blocking overlay.
  useEffect(() => {
    if (!state) return;
    if (state.status === "not_started" && variant === "full") {
      (async () => {
        try {
          const { data } = await api.post("/coach/day-zero/open");
          setState(data);
        } catch { /* silent */ }
      })();
    }
  }, [state?.status, variant]);

  const submitAnswer = async (stepId, answer) => {
    setBusy(true);
    try {
      const { data } = await api.post("/coach/day-zero/answer",
        { step_id: stepId, answer });
      setState(data);
      setTextAnswer("");
      if (data.status === "completed") {
        toast.success("Day Zero saved — Coach is ready.");
        onCompleted && onCompleted();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't save that answer.");
    } finally {
      setBusy(false);
    }
  };

  const doSkip = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coach/day-zero/skip");
      setState(data);
      setConfirmSkip(false);
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coach/day-zero/reset");
      setState(data);
    } finally { setBusy(false); }
  };

  if (loading || !state) return null;

  // ── MINI variant (Dashboard) ─────────────────────────────────
  if (variant === "mini") {
    if (state.status === "completed") return null;
    if (state.status === "abandoned") return null;   // don't nag daily
    return (
      <Link
        to="/coach"
        data-testid="day-zero-mini"
        className="flex items-center justify-between gap-3 p-4 border border-[var(--bt-accent)] rounded-sm hover:bg-[var(--bt-surface)] transition-colors group"
      >
        <div className="flex items-center gap-3">
          <Compass className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label text-[var(--bt-accent)]">
              Complete your Day Zero setup
            </p>
            <p className="text-sm text-[var(--bt-text)] mt-0.5">
              Five quick questions so Coach knows what to watch for.
              Step {state.step}/{state.total_steps}.
            </p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--bt-text-muted)] group-hover:text-[var(--bt-accent)] transition-colors" />
      </Link>
    );
  }

  // ── FULL variant (Coach) ─────────────────────────────────────
  // iter134 D5a fix — self-hide on abandoned status too (mirrors
  // the mini variant's "don't nag daily" rule). User dismissed the
  // banner — respect it until the SKIP_REPROMPT_DAYS window elapses
  // (server-side gate; UI just checks status).
  if (state.status === "abandoned") return null;

  if (state.status === "completed") {
    return (
      <section
        data-testid="day-zero-banner"
        className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-3"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <Check className="w-4 h-4 text-[var(--bt-accent)]" />
            <div>
              <p className="bt-label text-[var(--bt-accent)]">Day Zero · complete</p>
              <p
                data-testid="day-zero-welcome"
                className="text-sm text-[var(--bt-text)] mt-1 max-w-2xl"
              >
                {state.welcome_text}
              </p>
              <p className="text-xs text-[var(--bt-text-muted)] mt-2">
                Your Session Contract now shows a{" "}
                <span className="text-[var(--bt-accent)]">profile hint</span>{" "}
                beside each limit — you still pick the actual numbers.
              </p>
            </div>
          </div>
          <button
            data-testid="day-zero-reset-btn"
            onClick={doReset}
            disabled={busy}
            title="Reset and answer again"
            className="text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Redo
          </button>
        </div>
      </section>
    );
  }

  const step = state.next_step;
  const messages = state.messages || [];

  return (
    <section
      data-testid="day-zero-banner"
      className="bg-[var(--bt-surface)] border border-[var(--bt-accent)] rounded-sm p-6 space-y-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Compass className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label text-[var(--bt-accent)]">Day Zero setup</p>
            <h2 className="font-heading text-xl">Coach wants to know you</h2>
            <p className="text-xs text-[var(--bt-text-muted)] mt-1 max-w-2xl">
              Five short questions — no advice, no scoring. Just so Coach knows
              what "normal" looks like for you before your first trade lands.
              Step {Math.min(state.step + 1, state.total_steps)}/{state.total_steps}.
            </p>
          </div>
        </div>
        {!confirmSkip ? (
          <button
            data-testid="day-zero-skip-btn"
            onClick={() => setConfirmSkip(true)}
            disabled={busy}
            className="text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Skip for now
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--bt-text-muted)]">
              Coach works better with these — skip anyway?
            </span>
            <Button
              data-testid="day-zero-skip-confirm-btn"
              onClick={doSkip}
              disabled={busy}
              className="h-7 px-3 text-xs bg-transparent border border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] rounded-sm"
            >
              Yes, skip
            </Button>
            <button
              onClick={() => setConfirmSkip(false)}
              className="text-xs text-[var(--bt-text-muted)] underline"
            >
              Never mind
            </button>
          </div>
        )}
      </div>

      {/* Chat log */}
      <ul className="space-y-2" data-testid="day-zero-messages">
        {messages.map((m, idx) => (
          <li
            key={idx}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`day-zero-msg-${m.role}-${idx}`}
          >
            <div
              className={`max-w-[85%] px-3 py-2 rounded-sm text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[var(--bt-accent)] text-black font-medium"
                  : "border border-[var(--bt-border)] text-[var(--bt-text)]"
              }`}
            >
              {m.content}
            </div>
          </li>
        ))}
      </ul>

      {/* Active step — choice options or text field */}
      {step && (
        <div
          className="border border-[var(--bt-border)] rounded-sm p-4 space-y-3"
          data-testid={`day-zero-step-${step.id}`}
        >
          {step.answer_type === "choice" ? (
            <div className="flex flex-wrap gap-2">
              {(step.options || []).map((opt) => (
                <Button
                  key={opt.value}
                  data-testid={`day-zero-option-${opt.value}`}
                  disabled={busy}
                  onClick={() => submitAnswer(step.id, opt.value)}
                  className="bg-transparent border border-[var(--bt-border)] hover:border-[var(--bt-accent)] hover:text-[var(--bt-accent)] text-[var(--bt-text)] font-mono text-xs rounded-sm h-9 px-3"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : opt.label}
                </Button>
              ))}
            </div>
          ) : (
            <>
              <textarea
                data-testid="day-zero-text-input"
                value={textAnswer}
                onChange={(e) =>
                  setTextAnswer(e.target.value.slice(0, step.max_chars || 200))
                }
                maxLength={step.max_chars || 200}
                placeholder="One sentence — the habit Coach should watch for."
                rows={2}
                className="w-full font-sans text-sm leading-relaxed text-white outline-none resize-none"
                style={{
                  background: "#0f1a14",
                  border: "1px solid #1a2e1f",
                  borderRadius: "8px",
                  padding: "12px",
                  transition: "border-color 200ms ease",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#00ff88"; }}
                onBlur={(e) => { e.target.style.borderColor = "#1a2e1f"; }}
              />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-mono text-[var(--bt-text-muted)]">
                  {textAnswer.length} / {step.max_chars || 200}
                </span>
                <Button
                  data-testid="day-zero-submit-btn"
                  disabled={busy || !textAnswer.trim()}
                  onClick={() => submitAnswer(step.id, textAnswer)}
                  className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-9"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
